import {
  Box,
  FormControl,
  FormLabel,
  Grid2 as Grid,
  TextField,
  Typography,
  FormControlLabel,
  Switch,
  Chip,
  Stack,
  Card,
  CardContent,
  Paper,
  alpha,
  useTheme,
  Tooltip,
} from "@mui/material";
import { FormProvider } from "react-hook-form";
import { SaveButton } from "@/organism";
import {
  AccessTime as AccessTimeIcon,
  LocationOn as LocationOnIcon,
  Link as LinkIcon,
  Settings as SettingsIcon,
  Business as BusinessIcon,
  Info as InfoIcon,
  CorporateFare as CorporateFareIcon,
} from "@mui/icons-material";

import { Yn } from "@/constants/code";
import { CompanyStampDetailDto } from "@/dto/CompanyStampDto";
import useDetail from "./useDetail";

export type DetailProps = {
  companyStampInfo: CompanyStampDetailDto;
};

export const Detail = (props: DetailProps) => {
  const { companyStampInfo } = props;
  const theme = useTheme();

  const { methods, handleSubmit } = useDetail(companyStampInfo);

  const defaultProps = {
    size: "small" as const,
    error: false,
    helperText: "",
    placeholder: "입력해주세요",
  };

  /**
   * 구독 상태에 따른 Chip 컬러 반환
   */
  const getStatusColor = (status: string | null) => {
    if (!status) return "default";

    switch (status) {
      case "ACTIVE":
        return "success";
      case "PENDING":
        return "warning";
      case "CANCELED":
        return "error";
      default:
        return "default";
    }
  };

  /**
   * 날짜를 지정된 시간대로 변환하는 함수
   */
  const formatDateToTimezone = (
    dateString: string,
    timezone: string,
    label: string
  ) => {
    if (!dateString) return "-";

    try {
      // 입력된 날짜 문자열을 Date 객체로 변환 (UTC 기준으로 간주)
      const originalDate = new Date(dateString);
      if (isNaN(originalDate.getTime())) {
        return "-";
      }

      // 서머타임 여부 확인 함수
      const isDST = (date: Date, tzName: string): boolean => {
        if (tzName !== "America/New_York" && tzName !== "America/Los_Angeles") {
          return false;
        }

        // 미국 서머타임 규칙: 3월 둘째 일요일부터 11월 첫째 일요일까지
        const year = date.getUTCFullYear();

        // 3월 두 번째 일요일 계산
        const dstStart = new Date(Date.UTC(year, 2, 8, 7, 0, 0));
        dstStart.setUTCDate(8 + ((7 - dstStart.getUTCDay()) % 7));

        // 11월 첫 번째 일요일 계산
        const dstEnd = new Date(Date.UTC(year, 10, 1, 6, 0, 0));
        dstEnd.setUTCDate(1 + ((7 - dstEnd.getUTCDay()) % 7));

        return date >= dstStart && date < dstEnd;
      };

      // 시간대별 약어 및 오프셋 정보
      const getTimezoneInfo = (tz: string) => {
        let info = { abbr: tz, offset: "00:00" };

        switch (tz) {
          case "UTC":
            info = { abbr: "UTC", offset: "+00:00" };
            break;
          case "America/New_York": {
            const nyDst = isDST(originalDate, tz);
            info = {
              abbr: nyDst ? "EDT" : "EST",
              offset: nyDst ? "-04:00" : "-05:00",
            };
            break;
          }
          case "America/Los_Angeles": {
            const laDst = isDST(originalDate, tz);
            info = {
              abbr: laDst ? "PDT" : "PST",
              offset: laDst ? "-07:00" : "-08:00",
            };
            break;
          }
          case "Asia/Seoul":
            info = { abbr: "KST", offset: "+09:00" };
            break;
        }

        return info;
      };

      // 시간대별 오프셋 (UTC 기준, 분 단위)
      const getTimezoneOffset = (tz: string): number => {
        switch (tz) {
          case "UTC":
            return 0; // UTC: 0
          case "America/New_York":
            // 미국 동부: -5시간 (EST) 또는 -4시간 (EDT)
            return isDST(originalDate, tz) ? -4 * 60 : -5 * 60;
          case "America/Los_Angeles":
            // 미국 서부: -8시간 (PST) 또는 -7시간 (PDT)
            return isDST(originalDate, tz) ? -7 * 60 : -8 * 60;
          case "Asia/Seoul":
            return 9 * 60; // 한국: +9시간
          default:
            return 0;
        }
      };

      // 오프셋을 적용한 새 날짜 생성
      const offsetMinutes = getTimezoneOffset(timezone);
      const tzDate = new Date(originalDate.getTime() + offsetMinutes * 60000);

      // 날짜 형식화 (보기 좋게)
      const pad = (num: number) => num.toString().padStart(2, "0");
      const tzInfo = getTimezoneInfo(timezone);

      // 수동으로 날짜 형식화
      const formattedDate = `${tzDate.getUTCFullYear()}-${pad(
        tzDate.getUTCMonth() + 1
      )}-${pad(tzDate.getUTCDate())} ${pad(tzDate.getUTCHours())}:${pad(
        tzDate.getUTCMinutes()
      )}:${pad(tzDate.getUTCSeconds())}`;

      return (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography
            variant="caption"
            sx={{
              fontWeight: "medium",
              minWidth: "70px",
              color: "text.secondary",
            }}
          >
            {label}
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <Typography variant="body2" fontFamily="monospace">
              {formattedDate}
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ ml: 0.5 }}
            >
              ({tzInfo.abbr})
            </Typography>
            <Tooltip title={`UTC 기준 오프셋: ${tzInfo.offset}`}>
              <InfoIcon
                sx={{
                  fontSize: 16,
                  color: "text.secondary",
                  ml: 0.5,
                  cursor: "help",
                }}
              />
            </Tooltip>
          </Box>
        </Box>
      );
    } catch (error) {
      console.error(`Error formatting date for timezone ${timezone}:`, error);
      return (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography
            variant="caption"
            sx={{
              fontWeight: "medium",
              minWidth: "70px",
              color: "text.secondary",
            }}
          >
            {label}
          </Typography>
          <Typography variant="body2" color="error.main">
            변환 오류
          </Typography>
        </Box>
      );
    }
  };

  // 섹션 타이틀 컴포넌트
  const SectionTitle = ({
    icon,
    title,
  }: {
    icon: React.ReactNode;
    title: string;
  }) => (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        mb: 2,
        py: 1,
        borderBottom: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
      }}
    >
      {icon}
      <Typography variant="subtitle1" fontWeight="600" color="primary.main">
        {title}
      </Typography>
    </Box>
  );

  return (
    <FormProvider {...methods}>
      <Box display="flex" flexDirection="column" gap={3}>
        <Card variant="outlined" sx={{ mb: 2 }}>
          <CardContent>
            <SectionTitle
              icon={<BusinessIcon color="primary" />}
              title="기본 정보"
            />
            <Grid container spacing={3}>
              <Grid size={{ xs: 12, lg: 6 }}>
                <FormControl fullWidth>
                  <FormLabel
                    sx={{ fontWeight: 500, mb: 1, color: "text.secondary" }}
                  >
                    도장 ID
                  </FormLabel>
                  <TextField
                    {...methods.register("comId")}
                    {...defaultProps}
                    InputProps={{
                      readOnly: true,
                      sx: { bgcolor: "action.hover", fontFamily: "monospace" },
                    }}
                  />
                </FormControl>
              </Grid>
              <Grid size={{ xs: 12, lg: 6 }}>
                <FormControl fullWidth>
                  <FormLabel
                    required
                    sx={{ fontWeight: 500, mb: 1, color: "text.secondary" }}
                  >
                    도장명
                  </FormLabel>
                  <Box sx={{ display: "flex", flexDirection: "column" }}>
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <TextField
                        {...methods.register("comNm")}
                        {...defaultProps}
                        sx={{ flex: 1 }}
                      />
                      {methods.watch("branchYn") === "Y" && (
                        <Chip
                          label="지점"
                          size="small"
                          color="info"
                          sx={{ ml: 1 }}
                        />
                      )}
                    </Box>
                    {methods.watch("branchYn") === "Y" &&
                      companyStampInfo.parentsComNm && (
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            mt: 1,
                            color: "text.secondary",
                            fontSize: "0.85rem",
                          }}
                        >
                          <CorporateFareIcon
                            sx={{ fontSize: "1rem", mr: 0.5, opacity: 0.7 }}
                          />
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 500,
                              color: "primary.main",
                            }}
                          >
                            본점: {companyStampInfo.parentsComNm}
                          </Typography>
                        </Box>
                      )}
                  </Box>
                </FormControl>
              </Grid>
              <Grid size={{ xs: 12, lg: 6 }}>
                <FormControl fullWidth>
                  <FormLabel
                    required
                    sx={{ fontWeight: 500, mb: 1, color: "text.secondary" }}
                  >
                    사업자등록번호
                  </FormLabel>
                  <TextField
                    {...methods.register("bizrno")}
                    {...defaultProps}
                  />
                </FormControl>
              </Grid>
              <Grid size={{ xs: 12, lg: 6 }}>
                <FormControl fullWidth>
                  <FormLabel
                    required
                    sx={{ fontWeight: 500, mb: 1, color: "text.secondary" }}
                  >
                    대표자명
                  </FormLabel>
                  <TextField
                    {...methods.register("rprsntvNm")}
                    {...defaultProps}
                  />
                </FormControl>
              </Grid>
              <Grid size={{ xs: 12, lg: 6 }}>
                <FormControl fullWidth>
                  <FormLabel
                    required
                    sx={{ fontWeight: 500, mb: 1, color: "text.secondary" }}
                  >
                    주소
                  </FormLabel>
                  <TextField {...methods.register("addr")} {...defaultProps} />
                </FormControl>
              </Grid>
              <Grid size={{ xs: 12, lg: 6 }}>
                <FormControl fullWidth>
                  <FormLabel
                    sx={{ fontWeight: 500, mb: 1, color: "text.secondary" }}
                  >
                    상세주소
                  </FormLabel>
                  <TextField
                    {...methods.register("dtlAddr")}
                    {...defaultProps}
                  />
                </FormControl>
              </Grid>
              <Grid size={{ xs: 12, lg: 6 }}>
                <FormControl fullWidth>
                  <FormLabel
                    sx={{ fontWeight: 500, mb: 1, color: "text.secondary" }}
                  >
                    이메일
                  </FormLabel>
                  <TextField
                    type="email"
                    {...methods.register("email")}
                    {...defaultProps}
                  />
                </FormControl>
              </Grid>
              <Grid size={{ xs: 12, lg: 6 }}>
                <FormControl fullWidth>
                  <FormLabel
                    sx={{ fontWeight: 500, mb: 1, color: "text.secondary" }}
                  >
                    전화번호
                  </FormLabel>
                  <TextField
                    {...methods.register("telNum")}
                    {...defaultProps}
                  />
                </FormControl>
              </Grid>
              <Grid size={{ xs: 12, lg: 6 }}>
                <FormControl fullWidth>
                  <FormLabel
                    sx={{ fontWeight: 500, mb: 1, color: "text.secondary" }}
                  >
                    관리자명
                  </FormLabel>
                  <TextField {...methods.register("admNm")} {...defaultProps} />
                </FormControl>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        <Card variant="outlined" sx={{ mb: 2 }}>
          <CardContent>
            <SectionTitle
              icon={<AccessTimeIcon color="primary" />}
              title="구독 정보"
            />
            <Grid container spacing={3}>
              <Grid size={{ xs: 12, lg: 6 }}>
                <FormControl fullWidth>
                  <FormLabel
                    sx={{ fontWeight: 500, mb: 1, color: "text.secondary" }}
                  >
                    상품명
                  </FormLabel>
                  <TextField
                    {...methods.register("productNm")}
                    {...defaultProps}
                  />
                </FormControl>
              </Grid>
              <Grid size={{ xs: 12, lg: 6 }}>
                <FormLabel
                  sx={{ fontWeight: 500, mb: 1, color: "text.secondary" }}
                >
                  구독 상태
                </FormLabel>
                <Box sx={{ mt: 1 }}>
                  {methods.watch("subscriptionStat") ? (
                    <Chip
                      label={methods.watch("subscriptionStat")}
                      color={getStatusColor(methods.watch("subscriptionStat"))}
                      size="small"
                      sx={{ fontWeight: 500 }}
                    />
                  ) : (
                    "-"
                  )}
                </Box>
              </Grid>
              <Grid size={{ xs: 12, lg: 6 }}>
                <FormControl fullWidth>
                  <FormLabel
                    sx={{ fontWeight: 500, mb: 1, color: "text.secondary" }}
                  >
                    템플릿명
                  </FormLabel>
                  <TextField
                    {...methods.register("templateNm")}
                    {...defaultProps}
                  />
                </FormControl>
              </Grid>
              <Grid size={{ xs: 12, lg: 6 }}>
                <FormLabel
                  sx={{ fontWeight: 500, mb: 1, color: "text.secondary" }}
                >
                  매핑 등록일시
                </FormLabel>
                <Paper
                  variant="outlined"
                  sx={{
                    p: 1.5,
                    mt: 1,
                    bgcolor: alpha(theme.palette.primary.main, 0.03),
                    borderRadius: 1,
                  }}
                >
                  <Stack spacing={0.8}>
                    {formatDateToTimezone(
                      methods.watch("mapRegDttm"),
                      "UTC",
                      "UTC"
                    )}
                    {formatDateToTimezone(
                      methods.watch("mapRegDttm"),
                      "America/New_York",
                      "미국 동부"
                    )}
                    {formatDateToTimezone(
                      methods.watch("mapRegDttm"),
                      "America/Los_Angeles",
                      "미국 서부"
                    )}
                    {formatDateToTimezone(
                      methods.watch("mapRegDttm"),
                      "Asia/Seoul",
                      "한국"
                    )}
                  </Stack>
                </Paper>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        <Card variant="outlined" sx={{ mb: 2 }}>
          <CardContent>
            <SectionTitle
              icon={<LocationOnIcon color="primary" />}
              title="위치 정보"
            />
            <Grid container spacing={3}>
              <Grid size={{ xs: 12, lg: 6 }}>
                <FormControl fullWidth>
                  <FormLabel
                    sx={{ fontWeight: 500, mb: 1, color: "text.secondary" }}
                  >
                    경도
                  </FormLabel>
                  <TextField
                    {...methods.register("lon")}
                    {...defaultProps}
                    InputProps={{
                      sx: { fontFamily: "monospace" },
                    }}
                  />
                </FormControl>
              </Grid>
              <Grid size={{ xs: 12, lg: 6 }}>
                <FormControl fullWidth>
                  <FormLabel
                    sx={{ fontWeight: 500, mb: 1, color: "text.secondary" }}
                  >
                    위도
                  </FormLabel>
                  <TextField
                    {...methods.register("lat")}
                    {...defaultProps}
                    InputProps={{
                      sx: { fontFamily: "monospace" },
                    }}
                  />
                </FormControl>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        <Card variant="outlined" sx={{ mb: 2 }}>
          <CardContent>
            <SectionTitle
              icon={<LinkIcon color="primary" />}
              title="URL 정보"
            />
            <Grid container spacing={3}>
              <Grid size={{ xs: 12, lg: 6 }}>
                <FormControl fullWidth>
                  <FormLabel
                    sx={{ fontWeight: 500, mb: 1, color: "text.secondary" }}
                  >
                    유튜브 URL
                  </FormLabel>
                  <TextField
                    {...methods.register("youtubeUrl")}
                    {...defaultProps}
                  />
                </FormControl>
              </Grid>
              <Grid size={{ xs: 12, lg: 6 }}>
                <FormControl fullWidth>
                  <FormLabel
                    sx={{ fontWeight: 500, mb: 1, color: "text.secondary" }}
                  >
                    인스타그램 URL
                  </FormLabel>
                  <TextField
                    {...methods.register("instagramUrl")}
                    {...defaultProps}
                  />
                </FormControl>
              </Grid>
              <Grid size={{ xs: 12, lg: 6 }}>
                <FormControl fullWidth>
                  <FormLabel
                    sx={{ fontWeight: 500, mb: 1, color: "text.secondary" }}
                  >
                    페이스북 URL
                  </FormLabel>
                  <TextField
                    {...methods.register("facebookUrl")}
                    {...defaultProps}
                  />
                </FormControl>
              </Grid>
              <Grid size={{ xs: 12, lg: 6 }}>
                <FormControl fullWidth>
                  <FormLabel
                    sx={{ fontWeight: 500, mb: 1, color: "text.secondary" }}
                  >
                    홈페이지 URL
                  </FormLabel>
                  <TextField
                    {...methods.register("hmpgUrl")}
                    {...defaultProps}
                  />
                </FormControl>
              </Grid>
              <Grid size={{ xs: 12, lg: 6 }}>
                <FormControl fullWidth>
                  <FormLabel
                    sx={{ fontWeight: 500, mb: 1, color: "text.secondary" }}
                  >
                    도메인 URL
                  </FormLabel>
                  <TextField
                    {...methods.register("domainUrl")}
                    {...defaultProps}
                  />
                </FormControl>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        <Card variant="outlined" sx={{ mb: 2 }}>
          <CardContent>
            <SectionTitle
              icon={<SettingsIcon color="primary" />}
              title="추가 설정"
            />
            <Grid container spacing={3}>
              <Grid size={{ xs: 12, lg: 6 }}>
                <Box sx={{ p: 1 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={methods.watch("branchYn") === Yn.Y}
                        onChange={(e) =>
                          methods.setValue(
                            "branchYn",
                            e.target.checked ? Yn.Y : Yn.N
                          )
                        }
                        color="primary"
                      />
                    }
                    label={<Typography fontWeight={500}>지점 여부</Typography>}
                  />
                </Box>
              </Grid>
              <Grid size={{ xs: 12, lg: 6 }}>
                <Box sx={{ p: 1 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={methods.watch("dispYn") === Yn.Y}
                        onChange={(e) =>
                          methods.setValue(
                            "dispYn",
                            e.target.checked ? Yn.Y : Yn.N
                          )
                        }
                        color="primary"
                      />
                    }
                    label={<Typography fontWeight={500}>표시 여부</Typography>}
                  />
                </Box>
              </Grid>
              <Grid size={{ xs: 12, lg: 6 }}>
                <FormControl fullWidth>
                  <FormLabel
                    sx={{ fontWeight: 500, mb: 1, color: "text.secondary" }}
                  >
                    표시 순서
                  </FormLabel>
                  <TextField
                    type="number"
                    {...methods.register("dispSn")}
                    {...defaultProps}
                  />
                </FormControl>
              </Grid>
              <Grid size={{ xs: 12, lg: 6 }}>
                <FormLabel
                  sx={{ fontWeight: 500, mb: 1, color: "text.secondary" }}
                >
                  등록일시
                </FormLabel>
                <Paper
                  variant="outlined"
                  sx={{
                    p: 1.5,
                    mt: 1,
                    bgcolor: alpha(theme.palette.primary.main, 0.03),
                    borderRadius: 1,
                  }}
                >
                  <Stack spacing={0.8}>
                    {formatDateToTimezone(
                      companyStampInfo.regDttm,
                      "UTC",
                      "UTC"
                    )}
                    {formatDateToTimezone(
                      companyStampInfo.regDttm,
                      "America/New_York",
                      "미국 동부"
                    )}
                    {formatDateToTimezone(
                      companyStampInfo.regDttm,
                      "America/Los_Angeles",
                      "미국 서부"
                    )}
                    {formatDateToTimezone(
                      companyStampInfo.regDttm,
                      "Asia/Seoul",
                      "한국"
                    )}
                  </Stack>
                </Paper>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        <Box
          display="flex"
          alignItems="center"
          justifyContent="center"
          mt={3}
          mb={2}
        >
          <SaveButton
            onClick={handleSubmit}
            sx={{
              px: 4,
              py: 1,
              fontWeight: 500,
              boxShadow: 2,
            }}
          >
            저장
          </SaveButton>
        </Box>
      </Box>
    </FormProvider>
  );
};
